<template>
  <div v-if="isMobilePaginationVisible" class="ui-pagination">
    <UIButton
      v-bind="mobileButtonAttrs"
      :disabled="isPreviousButtonDisabled"
      label="Previous"
      @click="handleClickPage(pageNo - 1)"
    />
    <UIButton
      v-bind="mobileButtonAttrs"
      :disabled="isNextButtonDisabled"
      label="Next"
      @click="handleClickPage(pageNo + 1)"
    />
  </div>
  <div v-else-if="isDesktopPaginationVisible" class="ui-pagination">
    <div class="ui-pagination__summary">
      <span>Showing</span>
      <strong v-text="summaryFrom" />
      <span>to</span>
      <strong v-text="summaryTo" />
      <span>of</span>
      <strong data-refid="paginationTotal" v-text="total" />
      <span>items</span>
    </div>
    <nav
      v-if="isPagingVisible"
      class="ui-pagination__paging"
      aria-label="Pagination"
    >
      <UIButton
        v-bind="desktopButtonAttrs"
        :leading="ChevronDoubleLeftIcon"
        :disabled="isFirstButtonDisabled"
        @click="handleClickPage(0)"
      />
      <UIButton
        v-bind="desktopButtonAttrs"
        :leading="ChevronLeftIcon"
        :disabled="isPreviousButtonDisabled"
        @click="handleClickPage(pageNo - 1)"
      />
      <PaginationButton
        v-for="page in pages"
        :key="page"
        :page
        :page-no
        v-bind="desktopButtonAttrs"
        @click="handleClickPage"
      />
      <UIButton
        v-bind="desktopButtonAttrs"
        :leading="ChevronRightIcon"
        :disabled="isNextButtonDisabled"
        @click="handleClickPage(pageNo + 1)"
      />
      <UIButton
        v-bind="desktopButtonAttrs"
        :leading="ChevronDoubleRightIcon"
        :disabled="isLastButtonDisabled"
        @click="handleClickPage(totalPages - 1)"
      />
    </nav>

    <div class="ui-pagination__page-size">
      <UIInputSelect
        v-model="selectedPageSize"
        :data="pageSizeOptions"
        :disabled="props.disabled"
        size="xs"
      />
      <span>per page</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { ButtonFill, ButtonSize, ButtonVariant } from '@types'

import { PAGINATION_PAGES_COUNT, PAGE_SIZE_OPTIONS } from './utils/const'

import { useResponsive } from '@/plugins/responsiveUI'

import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/vue/24/outline'
import { UIButton } from '@ui/buttons'
import { UIInputSelect } from '@ui/fields'
import PaginationButton from './components/Button.vue'

type Props = {
  pageNo?: number
  pageSize?: number
  total?: number
  disabled?: boolean
}

type Emits = {
  'click:page': [data: number]
  'update:pageSize': [data: number]
}

const { pageNo = 0, pageSize = 0, total = 0, ...props } = defineProps<Props>()
const emit = defineEmits<Emits>()

const { isMobile } = useResponsive()

const selectedPageSize = computed({
  get: () => String(pageSize),
  set: (value: string) => {
    const newSize = Number(value)
    if (newSize !== pageSize) {
      emit('update:pageSize', newSize)
    }
  },
})

const pageSizeOptions = computed(() =>
  PAGE_SIZE_OPTIONS.map(size => ({
    value: String(size),
    label: String(size),
  })),
)

const mobileButtonAttrs = computed(() => ({
  size: 'sm' as ButtonSize,
}))

const desktopButtonAttrs = computed(() => ({
  size: 'xs' as ButtonSize,
  variant: 'gray' as ButtonVariant,
  fill: 'outlined' as ButtonFill,
  disabled: props.disabled,
}))

const summaryFrom = computed(() => pageNo * pageSize + 1)
const summaryTo = computed(() => {
  const to = summaryFrom.value - 1 + pageSize
  return to > total ? total : to
})
const totalPages = computed(() => Math.ceil(total / pageSize))
const pages = computed(() => {
  const arrayOfPages = Array.from(
    Array(totalPages.value),
    (_, index) => index + 1,
  )
  if (arrayOfPages.length <= PAGINATION_PAGES_COUNT) {
    return arrayOfPages
  }
  const halfOfCount = PAGINATION_PAGES_COUNT / 2
  let startPage = pageNo - Math.floor(halfOfCount)
  let endPage = pageNo + Math.ceil(halfOfCount)
  if (startPage < 0) {
    endPage += Math.abs(startPage)
    startPage = 0
  }
  if (endPage > arrayOfPages.length) {
    startPage += arrayOfPages.length - endPage
    endPage = arrayOfPages.length
  }
  const result = arrayOfPages.slice(startPage, endPage)
  if (startPage > 0) {
    result.unshift(0)
  }
  if (endPage < arrayOfPages.length) {
    result.push(0)
  }
  return result
})

const isPaginationVisible = computed(() => summaryTo.value >= summaryFrom.value)
const isDesktopPaginationVisible = computed(() => !isMobile.value && isPaginationVisible.value)
const isMobilePaginationVisible = computed(() => isMobile.value && total > pageSize && isPagingVisible.value)

const isPagingVisible = computed(() => pages.value.length > 1)

const isFirstButtonDisabled = computed(() => props.disabled || pageNo <= 1)
const isPreviousButtonDisabled = computed(() => props.disabled || pageNo <= 0)
const isNextButtonDisabled = computed(() => props.disabled || pageNo >= totalPages.value - 1)
const isLastButtonDisabled = computed(() => props.disabled || pageNo >= totalPages.value - 2)

const handleClickPage = (page: number) => {
  emit('click:page', page)
}
</script>

<style scoped>
.ui-pagination {
  @apply flex justify-between items-center;
  @apply py-3 sm:py-2;
  @apply gap-3;

  &__summary {
    @apply flex items-center;
    @apply gap-1;
    @apply text-sm sm:text-xs text-light-text;

    strong {
      @apply font-medium;
    }
  }

  &__page-size {
    @apply flex items-center justify-end;
    @apply gap-1;
    @apply text-sm sm:text-xs text-light-text;

    :deep(.ui-input-select) {
      @apply w-14;
    }

    span {
      @apply whitespace-nowrap;
    }
  }

  &__paging {
    @apply isolate flex justify-center;
    @apply -space-x-px;

    > * {
      @apply aspect-square;
      @apply focus:z-20;
      @apply first:rounded-r-none last:rounded-l-none;

      &:not(:first-child):not(:last-child) {
        @apply rounded-none;
      }
    }
  }
}
</style>
